/**
 * Mobile UI controller — powers mobile.html.
 *
 * Handles the bottom-sheet drawer, tab switching, audio sources,
 * look presets, composition, effects toggles, reel recording, and
 * now-playing overlay text.
 */

import { AudioEngine } from "../audio/sources";
import { Renderer } from "../viz/renderer";
import type { BackgroundRenderer } from "../viz/background";
import {
  SCENES,
  scenesByMood,
  type ScenePreset,
  type SceneVariant,
} from "../viz/scenes/scenePresets";
import { applyScenePreset } from "../viz/scenes/applyScenePreset";
import {
  CanvasRecorder,
  suggestFilename,
} from "../record/capture";
import { RecordingCompositor } from "../record/recordingCompositor";
import {
  SOCIAL_PRESETS,
  getSocialPreset,
  getSocialOutputSize,
  suggestSocialFilename,
} from "../record/socialPresets";
import {
  NowPlayingOverlay,
  OverlayConfig,
  DEFAULT_OVERLAY,
  clampOverlayFontScale,
} from "./nowPlaying";
import {
  ensureOverlayFontLoaded,
  isFontFamily,
  populateOverlayFontSelect,
  type FontFamily,
} from "./overlayFonts";
import { haptic } from "./haptics";
import { shareFile } from "./share";
import { buildMobileAdvancedPanel } from "./mobileAdvancedPanel";
import { createPadContainer, type PadContainerHandle, type PadAudioFeatures } from "./padContainer";

// ---------------------------------------------------------------------------
// Local-storage keys for UX persistence
// ---------------------------------------------------------------------------

const LS_LAST_TAB = "pulsefx_mobile_last_tab";
const LS_LAYER_GROUPS_OPEN = "pulsefx_mobile_layer_groups_open";
const LS_SCENE_FAMILIES_OPEN = "pulsefx_mobile_scene_families_open";
const LS_OVERLAY = "pulsefx_mobile_overlay_v1";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MobileUIArgs {
  engine: AudioEngine;
  renderer: Renderer;
  background?: BackgroundRenderer | null;
}

// ---------------------------------------------------------------------------
// Quick-start aspect/length configs
// ---------------------------------------------------------------------------
//
// The canonical platform list lives in `record/socialPresets.ts` so the
// mobile Reel UI and the desktop Reel Studio share specs (aspect, length,
// filename slug, share text). Mobile only consumes `aspect` + `lengthSec`
// from the preset for the live framing change; filename + share text are
// applied in the record-stop handler below via `getSocialPreset`.
void SOCIAL_PRESETS;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function $<T extends HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Public init
// ---------------------------------------------------------------------------

export function initMobileUI({
  engine,
  renderer,
  background = null,
}: MobileUIArgs): void {
  const frame = document.getElementById("frame") as HTMLElement;

  // ---- Splash screen: fade out as soon as we're wired up ----
  // PWA launches show a blank canvas for ~1-2 frames; the splash masks
  // that with a branded gradient until init completes.
  const splash = document.getElementById("mbSplash");
  if (splash) {
    requestAnimationFrame(() => {
      splash.classList.add("is-ready");
      // Remove from DOM after the fade so it's not a layout/paint cost.
      setTimeout(() => splash.remove(), 1100);
    });
  }

  // ---- Overlay ----
  const overlay = new NowPlayingOverlay(frame);
  let overlayCfg: OverlayConfig = { ...DEFAULT_OVERLAY };
  try {
    const saved = JSON.parse(localStorage.getItem(LS_OVERLAY) ?? "null") as unknown;
    if (saved && typeof saved === "object") {
      const candidate = saved as Partial<OverlayConfig>;
      overlayCfg = {
        ...overlayCfg,
        enabled: candidate.enabled === true,
        title: typeof candidate.title === "string" ? candidate.title.slice(0, 120) : "",
        artist: typeof candidate.artist === "string" ? candidate.artist.slice(0, 120) : "",
        fontFamily: isFontFamily(candidate.fontFamily)
          ? candidate.fontFamily
          : overlayCfg.fontFamily,
        fontScale: clampOverlayFontScale(candidate.fontScale),
      };
    }
  } catch {
    /* ignore invalid or unavailable storage */
  }
  overlay.setConfig(overlayCfg);
  void ensureOverlayFontLoaded(overlayCfg.fontFamily);

  // -------- Empty state: hide once a source is loaded --------
  const emptyState = document.getElementById("mbEmptyState");
  let sourceLoaded = false;
  const markSourceLoaded = (): void => {
    if (sourceLoaded) return;
    sourceLoaded = true;
    if (emptyState) {
      emptyState.classList.add("is-hidden");
      setTimeout(() => emptyState.remove(), 400);
    }
    // Reveal the mini-player now that there's something to control.
    showMiniPlayer();
  };

  // -------- Drawer open/close (with real touch dragging) --------
  const drawer = $<HTMLElement>("mbDrawer");
  const drawerToggle = $<HTMLButtonElement>("mbDrawerToggle");
  const drawerPill = $<HTMLElement>("mbDrawerPill");
  let drawerOpen = false;

  const openDrawer = (): void => {
    drawerOpen = true;
    drawer.style.transform = "";
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    drawerToggle.classList.add("is-open");
    drawerToggle.setAttribute("aria-expanded", "true");
    haptic("light");
    // Hide the mini-player while the drawer is open (drawer has all controls).
    hideMiniPlayer();
  };

  const closeDrawer = (): void => {
    drawerOpen = false;
    drawer.style.transform = "";
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    drawerToggle.classList.remove("is-open");
    drawerToggle.setAttribute("aria-expanded", "false");
    haptic("light");
    if (sourceLoaded) showMiniPlayer();
  };

  drawerToggle.addEventListener("click", () => {
    void engine.resume();
    if (drawerOpen) closeDrawer();
    else openDrawer();
  });

  // Real-time drawer drag: follows the finger. The drag handle (the pill)
  // is the active region; the rest of the drawer scrolls normally so
  // users can use the controls without accidentally dismissing.
  let dragStartY = 0;
  let dragLastY = 0;
  let dragging = false;
  const dragHandle = drawerPill ?? drawer; // fall back to whole drawer
  dragHandle.addEventListener(
    "touchstart",
    (e) => {
      dragStartY = e.touches[0]?.clientY ?? 0;
      dragLastY = dragStartY;
      dragging = true;
      drawer.classList.add("is-dragging");
    },
    { passive: true },
  );
  dragHandle.addEventListener(
    "touchmove",
    (e) => {
      if (!dragging) return;
      const y = e.touches[0]?.clientY ?? 0;
      dragLastY = y;
      const dy = Math.max(0, y - dragStartY); // only drag downward
      drawer.style.transform = `translateY(${dy}px)`;
    },
    { passive: true },
  );
  dragHandle.addEventListener(
    "touchend",
    () => {
      if (!dragging) return;
      dragging = false;
      drawer.classList.remove("is-dragging");
      const dy = dragLastY - dragStartY;
      // Threshold: drag past 100px → close. Otherwise snap back open.
      if (dy > 100) {
        closeDrawer();
      } else {
        // Snap back to open by clearing the inline transform.
        drawer.style.transform = "";
      }
    },
    { passive: true },
  );

  // -------- Settings: drawer opacity, auto-fade, haptics --------
  // These are pure UX prefs (not visualizer state) so they live in
  // localStorage rather than renderer.params. The CSS uses
  // --mb-drawer-bg-opacity on documentElement; haptic + auto-fade are
  // read by the rest of this module.
  const SETTINGS_KEY = "pulsefx_mobile_settings_v1";
  interface MobileSettings {
    drawerOpacity: number;   // 0.30..1.00, drives the CSS var
    autoFadeDrawer: boolean; // fade drawer to 58% while interacting
    haptics: boolean;        // disable to silence vibration on Android
  }
  const DEFAULT_SETTINGS: MobileSettings = {
    drawerOpacity: 0.96,
    autoFadeDrawer: false,
    haptics: true,
  };
  let settings: MobileSettings = { ...DEFAULT_SETTINGS };
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) settings = { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  const persistSettings = (): void => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch {
      /* private mode etc. */
    }
  };
  const applyDrawerOpacity = (): void => {
    document.documentElement.style.setProperty(
      "--mb-drawer-bg-opacity",
      String(settings.drawerOpacity),
    );
  };
  applyDrawerOpacity();

  const drawerOpacityInput = $<HTMLInputElement>("mb-drawerOpacity");
  const drawerOpacityVal = $<HTMLElement>("mb-drawerOpacityVal");
  if (drawerOpacityInput) {
    drawerOpacityInput.value = String(settings.drawerOpacity);
    if (drawerOpacityVal) drawerOpacityVal.textContent = settings.drawerOpacity.toFixed(2);
    drawerOpacityInput.addEventListener("input", () => {
      settings.drawerOpacity = Number(drawerOpacityInput.value);
      applyDrawerOpacity();
      if (drawerOpacityVal) drawerOpacityVal.textContent = settings.drawerOpacity.toFixed(2);
      persistSettings();
    });
  }

  const autoFadeInput = $<HTMLInputElement>("mb-autoFadeDrawer");
  if (autoFadeInput) {
    autoFadeInput.checked = settings.autoFadeDrawer;
    autoFadeInput.addEventListener("change", () => {
      settings.autoFadeDrawer = autoFadeInput.checked;
      persistSettings();
      // If turning off mid-interaction, drop any active fade right away.
      if (!settings.autoFadeDrawer) drawer.classList.remove("is-previewing");
    });
  }

  const hapticsInput = $<HTMLInputElement>("mb-haptics");
  if (hapticsInput) {
    hapticsInput.checked = settings.haptics;
    hapticsInput.addEventListener("change", () => {
      settings.haptics = hapticsInput.checked;
      persistSettings();
      // Note: the haptic() helper itself is a no-op on iOS Safari today,
      // so this mainly affects Android Chrome users.
    });
  }

  // -------- Optional tutorial overlay --------
  // Available from Settings without interrupting the first creator session.
  const TUTORIAL_KEY = "pulsefx_tutorial_seen_v1";
  interface TutorialStep {
    title: string;
    body: string;
  }
  const TUTORIAL_STEPS: TutorialStep[] = [
    {
      title: "1. Pick your audio",
      body:
        "The <strong>Source</strong> tab loads music — Mic for live audio, Load File for a saved track, or paste a Media URL.",
    },
    {
      title: "2. Pick a scene",
      body:
        "The <strong>Look</strong> tab has 24+ hand-tuned scenes. Tap any pad to switch instantly. Tweak Layers + Fine Tuning if you want to dial it in.",
    },
    {
      title: "3. Record + share",
      body:
        "The <strong>Reel</strong> tab → pick TikTok / Reels / Spotify Canvas → tap Record → tap again to stop. The iOS share sheet pops up with your <strong>.mp4</strong>.",
    },
  ];
  const tutEl = document.getElementById("mbTutorial");
  const tutStepEl = document.getElementById("mb-tutorial-step");
  const tutTitleEl = document.getElementById("mb-tutorial-title");
  const tutBodyEl = document.getElementById("mb-tutorial-body");
  const tutSkipBtn = $<HTMLButtonElement>("mb-tutorial-skip");
  const tutNextBtn = $<HTMLButtonElement>("mb-tutorial-next");
  let tutIndex = 0;

  const showTutorialStep = (i: number): void => {
    if (!tutEl) return;
    tutIndex = i;
    const step = TUTORIAL_STEPS[i];
    if (tutStepEl) tutStepEl.textContent = `${i + 1} of ${TUTORIAL_STEPS.length}`;
    if (tutTitleEl) tutTitleEl.textContent = step.title;
    if (tutBodyEl) tutBodyEl.innerHTML = step.body;
    if (tutNextBtn) {
      tutNextBtn.textContent = i === TUTORIAL_STEPS.length - 1 ? "Got it" : "Next";
    }
  };
  const openTutorial = (): void => {
    if (!tutEl) return;
    showTutorialStep(0);
    tutEl.classList.add("is-open");
    tutEl.setAttribute("aria-hidden", "false");
    haptic("light");
  };
  const closeTutorial = (markSeen: boolean): void => {
    if (!tutEl) return;
    tutEl.classList.remove("is-open");
    tutEl.setAttribute("aria-hidden", "true");
    if (markSeen) {
      try {
        localStorage.setItem(TUTORIAL_KEY, "1");
      } catch {
        /* ignore */
      }
    }
  };
  tutNextBtn?.addEventListener("click", () => {
    haptic("tick");
    if (tutIndex < TUTORIAL_STEPS.length - 1) {
      // Open the relevant tab as the user advances so they SEE what we're
      // describing — Source on step 1, Look on step 2, Reel on step 3.
      const tabForStep = ["source", "look", "reel"][tutIndex + 1];
      if (tabForStep) switchToTab(tabForStep);
      // Make sure the drawer is open so the highlighted tab is visible.
      if (!drawerOpen) openDrawer();
      showTutorialStep(tutIndex + 1);
    } else {
      closeTutorial(true);
    }
  });
  tutSkipBtn?.addEventListener("click", () => {
    haptic("light");
    closeTutorial(true);
  });
  // "Replay" button in Settings.
  document.getElementById("mb-replayTutorial")?.addEventListener("click", () => {
    haptic("light");
    openTutorial();
  });

  // -------- Auto-fade drawer while adjusting (opt-in) --------
  // Replaces the old smart-shrink behavior — instead of moving the
  // drawer (which yanked controls out from under the user's finger),
  // we just fade the drawer's opacity to ~58% while a slider is being
  // touched. Layout stays put, finger stays on the same control, but
  // the live canvas reads through the chrome.
  let previewSnapBack: ReturnType<typeof setTimeout> | null = null;
  const enterPreview = (): void => {
    if (!settings.autoFadeDrawer) return;
    if (previewSnapBack !== null) {
      clearTimeout(previewSnapBack);
      previewSnapBack = null;
    }
    drawer.classList.add("is-previewing");
  };
  const queueExitPreview = (): void => {
    if (previewSnapBack !== null) clearTimeout(previewSnapBack);
    previewSnapBack = setTimeout(() => {
      drawer.classList.remove("is-previewing");
      previewSnapBack = null;
    }, 650);
  };
  const PREVIEW_SELECTOR =
    'input[type="range"], .mb-advSegBtn, .mb-zoomSlider, .mb-zoomBtn';
  const isPreviewTrigger = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false;
    return Boolean(target.closest(PREVIEW_SELECTOR));
  };
  drawer.addEventListener("touchstart", (e) => {
    if (isPreviewTrigger(e.target)) enterPreview();
  }, { passive: true, capture: true });
  drawer.addEventListener("input", (e) => {
    if (isPreviewTrigger(e.target)) enterPreview();
  });
  document.addEventListener("touchend", queueExitPreview, { passive: true });
  document.addEventListener("touchcancel", queueExitPreview, { passive: true });
  document.addEventListener("pointerup", queueExitPreview, { passive: true });

  // -------- Tab switching (with localStorage persistence) --------
  const tabs = document.querySelectorAll<HTMLButtonElement>(".mb-tab");
  const panels = document.querySelectorAll<HTMLElement>(".mb-tabPanel");

  // -------- Universal PadContainer (mounted lazily inside Look tab) --------
  // Lives in <details id="mb-padsDetails"> so the Look tab still opens fast
  // on Scenes. Mount on details-open AND when the Look tab is active; unmount
  // on tab switch-away or details close so the iframe stops using GPU/audio.
  const padsDetails = document.getElementById("mb-padsDetails") as HTMLDetailsElement | null;
  const padsHost = document.getElementById("mb-padsHost");
  let padsHandle: PadContainerHandle | null = null;
  const padsAudioSource = (): PadAudioFeatures => {
    const f = engine.read();
    return {
      bass: f.bass, mid: f.mid, treble: f.treble, level: f.level,
      beat: f.beat, bpm: f.bpm, beatCount: f.beatCount,
      t: performance.now(),
    };
  };
  const mountPads = (): void => {
    if (padsHandle || !padsHost) return;
    try {
      padsHandle = createPadContainer({
        root: padsHost,
        layout: "mobile",
        audioSource: padsAudioSource,
      });
    } catch (err) {
      console.error("[pads] mobile mount failed:", err);
    }
  };
  const unmountPads = (): void => {
    if (!padsHandle) return;
    try { padsHandle.destroy(); } catch { /* ignore */ }
    padsHandle = null;
    if (padsHost) padsHost.innerHTML = "";
  };
  if (padsDetails) {
    padsDetails.addEventListener("toggle", () => {
      if (padsDetails.open) mountPads(); else unmountPads();
    });
  }

  const switchToTab = (target: string): void => {
    tabs.forEach((t) => {
      const active = t.dataset.tab === target;
      t.classList.toggle("is-active", active);
      t.setAttribute("aria-selected", String(active));
    });
    panels.forEach((p) => {
      const match = p.id === `mbTab-${target}`;
      p.hidden = !match;
      p.classList.toggle("is-active", match);
    });
    // Tear down pads iframe whenever the user leaves the Look tab so it
    // isn't quietly burning GPU + holding audio in the background.
    if (target !== "look") {
      unmountPads();
    } else if (padsDetails?.open) {
      mountPads();
    }
    try {
      localStorage.setItem(LS_LAST_TAB, target);
    } catch {
      /* private mode etc. */
    }
  };

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      const target = tab.dataset.tab;
      if (!target) return;
      haptic("tick");
      switchToTab(target);
    });
  });

  // Restore last-used tab on launch.
  try {
    const lastTab = localStorage.getItem(LS_LAST_TAB);
    if (lastTab && ["source", "look", "reel", "settings"].includes(lastTab)) {
      switchToTab(lastTab);
    }
  } catch {
    /* ignore */
  }

  // -------- Layer-group disclosure: persist open/closed state --------
  // The user's preferred categories should stick across sessions so they
  // don't have to re-expand the same group every time.
  const layerGroups = document.querySelectorAll<HTMLDetailsElement>(".mb-layerGroup:not(.mb-sceneFamily)");
  let openGroups = new Set<string>();
  try {
    const saved = JSON.parse(localStorage.getItem(LS_LAYER_GROUPS_OPEN) ?? "[]");
    if (Array.isArray(saved)) openGroups = new Set(saved);
  } catch {
    /* ignore */
  }
  layerGroups.forEach((g, i) => {
    const key = (g.querySelector("summary")?.textContent ?? `g${i}`).trim();
    // Only override default `open` if we have saved state.
    if (openGroups.size > 0) g.open = openGroups.has(key);
    g.addEventListener("toggle", () => {
      haptic("tick");
      const updated = new Set<string>();
      layerGroups.forEach((g2, i2) => {
        const k = (g2.querySelector("summary")?.textContent ?? `g${i2}`).trim();
        if (g2.open) updated.add(k);
      });
      try {
        localStorage.setItem(LS_LAYER_GROUPS_OPEN, JSON.stringify([...updated]));
      } catch {
        /* ignore */
      }
    });
  });

  // -------- Mini-player floating bar --------
  // Visible when drawer is closed AND a source is loaded. Provides
  // one-tap access to scenes / play-pause / record without re-opening
  // the full drawer for casual use.
  const miniPlayer = document.getElementById("mbMiniPlayer");
  const miniSceneBtn = $<HTMLButtonElement>("mbMiniSceneBtn");
  const miniSceneName = $<HTMLElement>("mbMiniSceneName");
  const miniPlayBtn = $<HTMLButtonElement>("mbMiniPlayBtn");
  const miniRecBtn = $<HTMLButtonElement>("mbMiniRecBtn");

  const showMiniPlayer = (): void => {
    if (miniPlayer && sourceLoaded && !drawerOpen) {
      miniPlayer.classList.add("is-visible");
      miniPlayer.setAttribute("aria-hidden", "false");
      // Hide the drawer toggle pill — the mini-player has its own
      // entry point (the scene name) and stacking two pills looks busy.
      drawerToggle.style.display = "none";
    }
  };
  const hideMiniPlayer = (): void => {
    if (miniPlayer) {
      miniPlayer.classList.remove("is-visible");
      miniPlayer.setAttribute("aria-hidden", "true");
      drawerToggle.style.display = "";
    }
  };

  miniSceneBtn?.addEventListener("click", () => {
    haptic("light");
    openDrawer();
    switchToTab("look");
  });

  // Empty-state Get Started button → open drawer on Source tab.
  document.getElementById("mbStartBtn")?.addEventListener("click", () => {
    haptic("medium");
    void engine.resume();
    openDrawer();
    switchToTab("source");
  });

  // Tap the HUD to manually hide it (e.g. for cleaner-feeling preview).
  const hudEl = document.getElementById("hud");
  hudEl?.addEventListener("click", () => {
    hudEl.classList.toggle("is-tapped-hidden");
  });

  // -------- Audio sources --------
  const setSourceStatus = (text: string): void => {
    const el = $<HTMLElement>("mb-sourceStatus");
    if (el) el.textContent = text;
  };

  const setSourceActive = (kind: string): void => {
    document.querySelectorAll<HTMLButtonElement>(".mb-srcBtn").forEach((btn) => {
      btn.classList.remove("is-active");
    });
    const mic = $<HTMLButtonElement>("mb-sourceMic");
    const tab = $<HTMLButtonElement>("mb-sourceTab");
    if (kind === "mic" && mic) mic.classList.add("is-active");
    if (kind === "tab" && tab) tab.classList.add("is-active");
  };

  $<HTMLButtonElement>("mb-sourceMic")?.addEventListener("click", async () => {
    setSourceStatus("Requesting mic…");
    haptic("light");
    try {
      const info = await engine.useMicrophone();
      setSourceStatus(`● ${info.label}`);
      setSourceActive("mic");
      markSourceLoaded();
      haptic("medium");
    } catch (err) {
      setSourceStatus(`Mic error: ${err instanceof Error ? err.message : String(err)}`);
    }
  });

  const tabBtn = $<HTMLButtonElement>("mb-sourceTab");
  // Detect if tab audio is available; if not, show a hint.
  if (!(navigator.mediaDevices as MediaDevices & { getDisplayMedia?: unknown }).getDisplayMedia) {
    if (tabBtn) tabBtn.disabled = true;
    const hint = $<HTMLElement>("mb-tabHint");
    if (hint) hint.hidden = false;
  }
  tabBtn?.addEventListener("click", async () => {
    setSourceStatus("Requesting tab audio…");
    haptic("light");
    try {
      const info = await engine.useTabAudio();
      setSourceStatus(`● ${info.label}`);
      setSourceActive("tab");
      markSourceLoaded();
      haptic("medium");
    } catch (err) {
      setSourceStatus(err instanceof Error ? err.message : String(err));
    }
  });

  // ----- Play/pause button binding -----
  // Single source of truth: one click handler at a time on `mb-playPauseAudio`,
  // always pointed at the currently-loaded audio element. The replaceWith
  // trick wipes any previous listener so we never leak stacked handlers.
  const playBtn = $<HTMLButtonElement>("mb-playPauseAudio");

  const bindPlayButton = (audio: HTMLAudioElement | null): void => {
    if (!playBtn) return;
    if (!audio) {
      playBtn.hidden = true;
      return;
    }
    // Clone-and-replace strips any previous listener from prior file/URL loads.
    const fresh = playBtn.cloneNode(true) as HTMLButtonElement;
    playBtn.replaceWith(fresh);
    fresh.hidden = false;
    fresh.textContent = audio.paused ? "▶︎ Tap to play" : "⏸ Pause";

    fresh.addEventListener("click", async () => {
      try {
        // iOS Safari leaves the AudioContext suspended until a gesture-initiated
        // resume completes. Awaiting here keeps the gesture chain alive.
        await engine.resume();
        if (audio.paused) {
          await audio.play();
          fresh.textContent = "⏸ Pause";
        } else {
          audio.pause();
          fresh.textContent = "▶︎ Play";
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setSourceStatus(`Playback blocked: ${msg}`);
      }
    });

    audio.addEventListener("play", () => {
      fresh.textContent = "⏸ Pause";
      if (miniPlayBtn) miniPlayBtn.textContent = "⏸";
    });
    audio.addEventListener("pause", () => {
      fresh.textContent = "▶︎ Play";
      if (miniPlayBtn) miniPlayBtn.textContent = "▶︎";
    });
    audio.addEventListener("ended", () => {
      fresh.textContent = "▶︎ Play";
      if (miniPlayBtn) miniPlayBtn.textContent = "▶︎";
    });
  };

  // ----- Playback bar (scrub + A/B loop + beat-snap) -----
  // Bound whenever a file or URL source loads. The bar lives in the
  // Source tab and provides:
  //   - Live time / duration readout
  //   - Draggable scrub slider (jumps audio.currentTime)
  //   - ⟲5 / 5⟳ jump buttons
  //   - Loop toggle → reveals A/B sliders + beat-snap controls
  //   - Beat-snap: when on, A/B snap to the nearest bar boundary
  //     computed from the live BPM (so the loop is always musical)
  const playback = {
    container: document.getElementById("mb-playback"),
    timeEl: document.getElementById("mb-playback-time"),
    durEl: document.getElementById("mb-playback-duration"),
    scrub: $<HTMLInputElement>("mb-playback-scrub"),
    back: $<HTMLButtonElement>("mb-playback-back"),
    fwd: $<HTMLButtonElement>("mb-playback-fwd"),
    loop: $<HTMLInputElement>("mb-playback-loop"),
    loopRangeA: document.getElementById("mb-playback-loopRange"),
    loopRangeB: document.getElementById("mb-playback-loopRangeB"),
    loopStart: $<HTMLInputElement>("mb-playback-loopStart"),
    loopEnd: $<HTMLInputElement>("mb-playback-loopEnd"),
    setA: $<HTMLButtonElement>("mb-playback-setA"),
    setB: $<HTMLButtonElement>("mb-playback-setB"),
    beatSnapRow: document.getElementById("mb-playback-beatSnap"),
    snap: $<HTMLInputElement>("mb-playback-snap"),
    snapBars: $<HTMLSelectElement>("mb-playback-snapBars"),
  };
  /** Format seconds as M:SS. */
  const fmtTime = (sec: number): string => {
    if (!isFinite(sec) || sec < 0) sec = 0;
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };
  let playbackBoundAudio: HTMLAudioElement | null = null;
  let playbackPollHandle: ReturnType<typeof setInterval> | null = null;
  let userIsScrubbing = false;
  /** Snap a time (seconds) to the nearest bar boundary at current BPM. */
  const snapToBeat = (sec: number, bpm: number, bars: number): number => {
    if (!bpm || bpm <= 0) return sec;
    const beatLen = 60 / bpm;
    const barLen = beatLen * bars;
    return Math.round(sec / barLen) * barLen;
  };

  const refreshLoopRowVisibility = (): void => {
    const on = playback.loop?.checked ?? false;
    if (playback.loopRangeA) playback.loopRangeA.hidden = !on;
    if (playback.loopRangeB) playback.loopRangeB.hidden = !on;
    if (playback.beatSnapRow) playback.beatSnapRow.hidden = !on;
  };
  refreshLoopRowVisibility();
  playback.loop?.addEventListener("change", () => {
    haptic("tick");
    refreshLoopRowVisibility();
  });

  const bindPlaybackBar = (audio: HTMLAudioElement | null): void => {
    if (!playback.container) return;
    playbackBoundAudio = audio;
    if (!audio) {
      playback.container.hidden = true;
      if (playbackPollHandle) {
        clearInterval(playbackPollHandle);
        playbackPollHandle = null;
      }
      return;
    }
    playback.container.hidden = false;

    // Initialize duration once metadata loads.
    const updateDuration = (): void => {
      const dur = audio.duration;
      if (isFinite(dur) && dur > 0 && playback.durEl) {
        playback.durEl.textContent = fmtTime(dur);
      }
    };
    audio.addEventListener("loadedmetadata", updateDuration);
    if (audio.readyState >= 1) updateDuration();

    // Poll currentTime to drive the scrub slider + time readout. 200ms
    // is plenty smooth for a transport bar and stays cheap on battery.
    if (playbackPollHandle) clearInterval(playbackPollHandle);
    playbackPollHandle = setInterval(() => {
      if (!playbackBoundAudio) return;
      const a = playbackBoundAudio;
      const dur = a.duration;
      if (!isFinite(dur) || dur <= 0) return;
      const cur = a.currentTime;
      // Update scrub UNLESS the user is actively scrubbing it.
      if (!userIsScrubbing && playback.scrub) {
        playback.scrub.value = String((cur / dur) * 100);
      }
      if (playback.timeEl) playback.timeEl.textContent = fmtTime(cur);

      // Loop enforcement: if past loop end, jump back to loop start.
      if (playback.loop?.checked && playback.loopStart && playback.loopEnd) {
        let startPct = Number(playback.loopStart.value);
        let endPct = Number(playback.loopEnd.value);
        if (endPct <= startPct) endPct = Math.min(100, startPct + 1);
        let startSec = (startPct / 100) * dur;
        let endSec = (endPct / 100) * dur;
        // Apply beat snap if enabled and we have a BPM from the analyzer.
        if (playback.snap?.checked) {
          const features = engine.read();
          const bpm = features.bpm;
          const bars = Number(playback.snapBars?.value ?? 8);
          if (bpm > 0) {
            startSec = snapToBeat(startSec, bpm, bars);
            endSec = snapToBeat(endSec, bpm, bars);
            if (endSec - startSec < (60 / bpm) * bars) {
              // Ensure at least one bar of loop length.
              endSec = startSec + (60 / bpm) * bars;
            }
          }
        }
        if (cur >= endSec || cur < startSec) {
          a.currentTime = startSec;
        }
      }
    }, 200);
  };

  // Wire the scrub slider — drag = scrub through audio.
  playback.scrub?.addEventListener("pointerdown", () => { userIsScrubbing = true; });
  playback.scrub?.addEventListener("touchstart", () => { userIsScrubbing = true; }, { passive: true });
  const finishScrub = (): void => {
    if (!userIsScrubbing) return;
    userIsScrubbing = false;
    const a = playbackBoundAudio;
    if (!a || !playback.scrub) return;
    const dur = a.duration;
    if (!isFinite(dur) || dur <= 0) return;
    const pct = Number(playback.scrub.value);
    a.currentTime = (pct / 100) * dur;
  };
  playback.scrub?.addEventListener("pointerup", finishScrub);
  playback.scrub?.addEventListener("touchend", finishScrub, { passive: true });
  playback.scrub?.addEventListener("input", () => {
    // Live time readout while scrubbing (no seek until release for perf).
    const a = playbackBoundAudio;
    if (!a || !playback.timeEl) return;
    const dur = a.duration;
    if (!isFinite(dur) || dur <= 0) return;
    const pct = Number(playback.scrub.value);
    playback.timeEl.textContent = fmtTime((pct / 100) * dur);
  });

  // ⟲5 / 5⟳ jump buttons.
  playback.back?.addEventListener("click", () => {
    haptic("light");
    if (playbackBoundAudio) {
      playbackBoundAudio.currentTime = Math.max(0, playbackBoundAudio.currentTime - 5);
    }
  });
  playback.fwd?.addEventListener("click", () => {
    haptic("light");
    if (playbackBoundAudio) {
      const dur = playbackBoundAudio.duration;
      const next = playbackBoundAudio.currentTime + 5;
      playbackBoundAudio.currentTime = isFinite(dur) ? Math.min(dur, next) : next;
    }
  });
  // ⊙ A / ⊙ B → snap loop bound to current playhead.
  playback.setA?.addEventListener("click", () => {
    haptic("medium");
    if (playbackBoundAudio && playback.loopStart) {
      const dur = playbackBoundAudio.duration;
      if (isFinite(dur) && dur > 0) {
        playback.loopStart.value = String((playbackBoundAudio.currentTime / dur) * 100);
      }
    }
  });
  playback.setB?.addEventListener("click", () => {
    haptic("medium");
    if (playbackBoundAudio && playback.loopEnd) {
      const dur = playbackBoundAudio.duration;
      if (isFinite(dur) && dur > 0) {
        playback.loopEnd.value = String((playbackBoundAudio.currentTime / dur) * 100);
      }
    }
  });
  playback.snap?.addEventListener("change", () => haptic("tick"));

  $<HTMLInputElement>("mb-fileInput")?.addEventListener("change", async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    setSourceStatus(`Loading ${file.name}…`);
    try {
      // Wake the AudioContext inside the change-handler call stack so iOS
      // accepts subsequent audio.play() calls without a separate tap.
      await engine.resume();
      const info = engine.useFile(file);
      setSourceStatus(`● ${info.label}`);
      setSourceActive("file");
      bindPlayButton(info.mediaElement ?? null);
      bindPlaybackBar(info.mediaElement ?? null);
      markSourceLoaded();
      haptic("medium");
      void NowPlayingOverlay.readMetadataFromFile(file).then((meta) => {
        if (!overlayCfg.title) overlayCfg.title = meta.title;
        if (!overlayCfg.artist && meta.artist) overlayCfg.artist = meta.artist;
        if (overlayCfg.title || overlayCfg.artist) overlayCfg.enabled = true;
        const titleInput = $<HTMLInputElement>("mb-overlayTitle");
        const artistInput = $<HTMLInputElement>("mb-overlayArtist");
        const enabledInput = $<HTMLInputElement>("mb-overlayEnabled");
        if (titleInput) titleInput.value = overlayCfg.title;
        if (artistInput) artistInput.value = overlayCfg.artist;
        if (enabledInput) enabledInput.checked = overlayCfg.enabled;
        overlay.setConfig(overlayCfg);
        try {
          localStorage.setItem(LS_OVERLAY, JSON.stringify(overlayCfg));
        } catch {
          /* ignore */
        }
      });
      // Surface load errors (corrupt files, unsupported codecs).
      if (info.mediaElement) {
        info.mediaElement.addEventListener("error", () => {
          setSourceStatus(
            `Couldn't decode "${file.name}". Try MP3, M4A, WAV, or OGG.`,
          );
        });
      }
    } catch (err) {
      setSourceStatus(`File error: ${err instanceof Error ? err.message : String(err)}`);
    }
  });

  $<HTMLButtonElement>("mb-mediaUrlLoad")?.addEventListener("click", async () => {
    const input = $<HTMLInputElement>("mb-mediaUrl");
    const raw = input?.value.trim() ?? "";
    if (!raw) return;
    setSourceStatus("Loading URL…");
    try {
      await engine.resume();
      const info = engine.useRemoteUrl(raw);
      setSourceStatus(`● ${info.label}`);
      setSourceActive("url");
      bindPlayButton(info.mediaElement ?? null);
      bindPlaybackBar(info.mediaElement ?? null);
      markSourceLoaded();
      haptic("medium");
      if (info.mediaElement) {
        // Catch CORS / network failures from the audio element.
        info.mediaElement.addEventListener("error", () => {
          setSourceStatus(
            "URL blocked: the host doesn't allow direct playback (CORS). " +
              "Download the file and use 'Load audio file' instead.",
          );
        });
      }
    } catch (err) {
      setSourceStatus(`URL error: ${err instanceof Error ? err.message : String(err)}`);
    }
  });

  // Track which curated scene the user last applied so the active state
  // survives across preset taps without us having to deep-equal the params.
  let activeSceneName: string | null = null;
  let activeVariantName: string | null = null;
  const sceneButtons: Array<{ name: string; el: HTMLButtonElement }> = [];
  const refreshSceneActive = (): void => {
    for (const { name, el } of sceneButtons) {
      el.classList.toggle("is-active", name === activeSceneName);
    }
    renderVariantStrip();
  };

  const applyPresetClean = (
    scene: ScenePreset,
    variant: SceneVariant | null = null,
    variantName: string | null = null,
  ): void => {
    applyScenePreset(renderer, scene, variant);
    activeSceneName = scene.name;
    activeVariantName = variantName;
    refreshLookUI();
    refreshSceneActive();
    // Surface the active scene name in the mini-player.
    if (miniSceneName) {
      miniSceneName.textContent = variantName
        ? `${scene.name} · ${variantName}`
        : scene.name;
    }
    haptic("medium");
  };

  // -------- Look: curated scene pads --------
  // Single primary entry point. Each scene is a hand-tuned, self-contained
  // configuration — no chimera states from layering Particle Cloud + Hybrid
  // composition + Tunnel concept (which used to confuse the visuals).
  const scenePadsEl = $<HTMLElement>("mb-scenePads");
  // Variant chip strip is injected dynamically just under the pad grid so
  // we don't have to touch mobile.html. It only becomes visible when the
  // active scene actually has variants.
  let variantStripEl: HTMLDivElement | null = null;
  if (scenePadsEl?.parentElement) {
    variantStripEl = document.createElement("div");
    variantStripEl.className = "mb-variantStrip";
    variantStripEl.hidden = true;
    // Inline styles keep this self-contained — no CSS file edits required.
    variantStripEl.style.cssText =
      "display:flex;gap:6px;flex-wrap:wrap;padding:6px 0 0 0;";
    scenePadsEl.parentElement.insertBefore(variantStripEl, scenePadsEl.nextSibling);
  }

  const renderVariantStrip = (): void => {
    if (!variantStripEl) return;
    variantStripEl.innerHTML = "";
    const scene = activeSceneName
      ? SCENES.find((s) => s.name === activeSceneName)
      : null;
    if (!scene || !scene.variants?.length) {
      variantStripEl.hidden = true;
      return;
    }
    variantStripEl.hidden = false;
    const makeChip = (
      label: string,
      hue: number,
      isActive: boolean,
      onTap: () => void,
    ): HTMLButtonElement => {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "mb-variantChip" + (isActive ? " is-active" : "");
      chip.textContent = label;
      chip.style.cssText =
        "padding:6px 12px;border-radius:999px;font:500 12px/1 system-ui,sans-serif;" +
        `border:1px solid hsla(${hue}deg,80%,60%,${isActive ? 0.95 : 0.4});` +
        `background:hsla(${hue}deg,80%,${isActive ? 35 : 16}%,${isActive ? 0.55 : 0.25});` +
        `color:hsl(${hue}deg,90%,${isActive ? 92 : 78}%);cursor:pointer;`;
      chip.addEventListener("click", () => {
        onTap();
        haptic("light");
      });
      return chip;
    };
    // Base "Original"
    variantStripEl.appendChild(
      makeChip("Original", scene.accent, activeVariantName === null, () => {
        applyPresetClean(scene);
      }),
    );
    for (const v of scene.variants) {
      const hue = v.accent ?? scene.accent;
      variantStripEl.appendChild(
        makeChip(v.name, hue, activeVariantName === v.name, () => {
          applyPresetClean(scene, v, v.name);
        }),
      );
    }
  };

  if (scenePadsEl) {
    const engineHeading = document.createElement("div");
    engineHeading.className = "mb-sectionLabel mb-sectionLabel--sub";
    engineHeading.textContent = "Engines";
    engineHeading.style.cssText =
      "font:600 11px/1 system-ui,sans-serif;letter-spacing:.08em;" +
      "text-transform:uppercase;opacity:.6;margin:10px 0 6px 0;";
    scenePadsEl.appendChild(engineHeading);

    const engineRow = document.createElement("div");
    engineRow.className = "mb-padsTall mb-padsTall--row";
    engineRow.style.cssText =
      "display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:8px;";
    scenePadsEl.appendChild(engineRow);

    const galaxyBtn = document.createElement("button");
    galaxyBtn.type = "button";
    galaxyBtn.className = "mb-padTall";
    galaxyBtn.style.borderColor = "hsla(175deg, 90%, 62%, 0.5)";
    galaxyBtn.innerHTML = `
      <span class="mb-padTall__name" style="color:hsl(175deg, 90%, 78%)">Galaxy</span>
      <span class="mb-padTall__sub">Wall Lab particle river · main renderer</span>
    `;
    galaxyBtn.addEventListener("click", () => {
      renderer.sceneType = "galaxy";
      Object.assign(renderer.params, {
        paletteHue: 198,
        starSize: 0.34,
        opacity: 0.9,
        flowSpeed: 1.05,
        bassGravity: 1.4,
        midCurl: 1.25,
        trebleShimmer: 1.3,
        bloomEnabled: true,
        bloomStrength: 0.9,
        bloomRadius: 0.72,
        feedbackStrength: 0.04,
      });
      activeSceneName = "Galaxy";
      activeVariantName = null;
      refreshLookUI();
      refreshSceneActive();
      if (miniSceneName) miniSceneName.textContent = "Galaxy";
      haptic("medium");
    });
    engineRow.appendChild(galaxyBtn);
    sceneButtons.push({ name: "Galaxy", el: galaxyBtn });

    // Render mood-grouped sections so the pad grid feels curated, not flat.
    let sceneFamilyOpen = new Set<string>();
    try {
      const saved = JSON.parse(localStorage.getItem(LS_SCENE_FAMILIES_OPEN) ?? "[]");
      if (Array.isArray(saved)) sceneFamilyOpen = new Set(saved.map(String));
    } catch {
      /* ignore */
    }
    scenesByMood().forEach((group, groupIndex) => {
      const family = document.createElement("details");
      family.className = "mb-sceneFamily";
      family.open = sceneFamilyOpen.size > 0
        ? sceneFamilyOpen.has(String(group.mood))
        : groupIndex === 0;
      const heading = document.createElement("summary");
      heading.textContent = group.mood;
      family.appendChild(heading);
      const body = document.createElement("div");
      body.className = "mb-sceneFamily__body";
      family.appendChild(body);
      scenePadsEl.appendChild(family);
      const row = document.createElement("div");
      row.className = "mb-padsTall mb-padsTall--row";
      row.style.cssText =
        "display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:8px;";
      body.appendChild(row);
      family.addEventListener("toggle", () => {
        const updated = new Set<string>();
        scenePadsEl.querySelectorAll<HTMLDetailsElement>(".mb-sceneFamily").forEach((item) => {
          const key = (item.querySelector("summary")?.textContent ?? "").trim();
          if (item.open && key) updated.add(key);
        });
        try {
          localStorage.setItem(LS_SCENE_FAMILIES_OPEN, JSON.stringify([...updated]));
        } catch {
          /* ignore */
        }
      });
      for (const scene of group.scenes) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "mb-padTall";
        btn.style.borderColor = `hsla(${scene.accent}deg, 80%, 60%, 0.4)`;
        const variantHint = scene.variants?.length
          ? ` <span class="mb-padTall__variantBadge" style="opacity:.7;font-size:10px">×${scene.variants.length + 1}</span>`
          : "";
        btn.innerHTML = `
          <span class="mb-padTall__name" style="color:hsl(${scene.accent}deg, 80%, 78%)">${scene.name}${variantHint}</span>
          <span class="mb-padTall__sub">${scene.subtitle}</span>
        `;
        btn.addEventListener("click", () => applyPresetClean(scene));
        row.appendChild(btn);
        sceneButtons.push({ name: scene.name, el: btn });
      }
    });
  }
  // Touch types so unused-import lints stay quiet on builds where the
  // helper types are inferred via the variant-strip closure above.
  void (null as ScenePreset | SceneVariant | null);

  // -------- Look: per-layer toggles --------
  // The single source of truth for visibility. Each toggle drives one of
  // the layer* params and the renderer reads them every frame.
  type LayerKey =
    | "layerParticles"
    | "layerStrings"
    | "layerLattice"
    | "layerAurora"
    | "layerWarp"
    | "layerCathedral";
  const layerInputs = Array.from(
    document.querySelectorAll<HTMLInputElement>("#mb-layerList input[data-layer]"),
  );
  layerInputs.forEach((input) => {
    const key = input.dataset.layer as LayerKey | undefined;
    if (!key) return;
    input.addEventListener("change", () => {
      renderer.params[key] = input.checked;
      // Flipping any layer means we're no longer "exactly" on a scene preset.
      activeSceneName = null;
      activeVariantName = null;
      refreshSceneActive();
      haptic("tick");
    });
  });

  // -------- Look: effects toggles --------
  const bloomToggle = $<HTMLInputElement>("mb-bloomEnabled");
  bloomToggle?.addEventListener("change", () => {
    renderer.params.bloomEnabled = bloomToggle.checked;
  });

  // -------- Camera zoom (mobile slider) --------
  // Pinch-to-zoom on the canvas works but is finicky and the new wide
  // zoom range (0.15 lava-lamp .. 3.5 close-up) needed an explicit
  // surface so users can dial it precisely. The previous +/- buttons
  // were redundant with the slider + pinch and have been removed.
  const zoomSlider = $<HTMLInputElement>("mb-zoom");
  zoomSlider?.addEventListener("input", () => {
    renderer.params.cameraZoom = Number(zoomSlider.value);
  });

  const refreshLookUI = (): void => {
    // Sync layer toggles with current params (called after a scene apply).
    layerInputs.forEach((input) => {
      const key = input.dataset.layer as LayerKey | undefined;
      if (key) input.checked = Boolean(renderer.params[key]);
    });
    if (bloomToggle) bloomToggle.checked = renderer.params.bloomEnabled;
    if (zoomSlider) zoomSlider.value = String(renderer.params.cameraZoom);
    // Re-sync every advanced slider/toggle so a scene apply updates them.
    advPanel?.refresh();
  };

  // -------- Advanced (fine-tuning) controls --------
  // Mounts only controls that visibly affect the active scene.
  const advHost = document.getElementById("mb-advPanel");
  const advPanel = advHost
    ? buildMobileAdvancedPanel(advHost, renderer, () => {
        const scene = activeSceneName
          ? SCENES.find((candidate) => candidate.name === activeSceneName)
          : SCENES.find(
              (candidate) =>
                (candidate.sceneType ?? "bioclast") === renderer.sceneType,
            ) ?? SCENES[0];
          if (!scene) {
            return {
              sceneName: "Liquid Mass",
              sceneType: "bioclast",
              profile: "bioclast",
              values: {},
            };
          }
        return {
          sceneName: scene.name,
          sceneType: scene.sceneType ?? "bioclast",
          profile: scene.controlProfile,
          values: scene.values,
        };
      })
    : null;

  refreshLookUI();

  // Pinch on the canvas mutates cameraZoom directly — keep the slider in
  // sync so the on-screen control matches what the gesture did.
  setInterval(() => {
    if (
      zoomSlider &&
      Math.abs(Number(zoomSlider.value) - renderer.params.cameraZoom) > 0.01
    ) {
      zoomSlider.value = String(renderer.params.cameraZoom);
    }
  }, 250);

  // -------- Reel: quick-start --------
  let armedSocialPresetId: string | null = null;
  document.querySelectorAll<HTMLButtonElement>("[data-quickstart]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const key = btn.dataset.quickstart;
      const preset = getSocialPreset(key);
      if (!preset) return;
      frame.dataset.aspect = preset.aspect;
      window.dispatchEvent(new Event("resize"));
      lengthCapMs = preset.lengthSec * 1000;
      armedSocialPresetId = preset.id;
      // Highlight only the chosen platform chip until the next pick.
      document
        .querySelectorAll<HTMLButtonElement>("[data-quickstart]")
        .forEach((b) => b.classList.remove("is-armed"));
      btn.classList.add("is-armed");
      haptic("light");
    });
  });

  // -------- Reel: record --------
  const recorder = new CanvasRecorder();
  // Composite the WebGL canvas + Now Playing overlay (text + watermark)
  // into a single canvas so the recorder captures both. Without this,
  // `canvas.captureStream()` only sees WebGL pixels and silently drops
  // the DOM overlay.
  const compositor = new RecordingCompositor(renderer.getCanvas(), {
    background,
    nowPlaying: overlay,
  });
  let lengthCapMs = 0;
  let recordingStartedAt = 0;
  let timerHandle: ReturnType<typeof setInterval> | null = null;

  const setRecStatus = (text: string): void => {
    const el = $<HTMLElement>("mb-recordStatus");
    if (el) el.textContent = text;
  };

  const recordBtn = $<HTMLButtonElement>("mb-recordBtn");

  /**
   * Try to share via the native iOS share sheet (Capacitor) or Web Share
   * API (PWA), falling back to download if neither succeeds. Uses the
   * unified `shareFile` helper so behavior matches the desktop reel
   * studio + future native exports.
   */
  const offerShareOrDownload = async (
    blob: Blob,
    filename: string,
  ): Promise<void> => {
    const armed = getSocialPreset(armedSocialPresetId);
    await shareFile(blob, filename, {
      title: armed ? `PulseFX · ${armed.label}` : "PulseFX clip",
      text: armed?.shareText ?? "Made with PulseFX",
    });
    haptic("heavy");
  };

  const toggleRecord = async (): Promise<void> => {
    if (recorder.isRecording()) {
      try {
        const result = await recorder.stop();
        compositor.stop();
        const armed = getSocialPreset(armedSocialPresetId);
        const filename = armed
          ? suggestSocialFilename(result.extension, armed)
          : suggestFilename(result.extension);
        // Try the share sheet first (works on Android + iOS for files
        // when the gesture chain is intact). Falls back to download.
        await offerShareOrDownload(result.blob, filename);
        setRecStatus(
          `Saved (${(result.blob.size / 1_000_000).toFixed(1)} MB, ${(result.durationMs / 1000).toFixed(1)}s).`,
        );
      } catch (err) {
        compositor.stop();
        setRecStatus(`Error: ${err instanceof Error ? err.message : String(err)}`);
      }
      if (timerHandle !== null) {
        clearInterval(timerHandle);
        timerHandle = null;
      }
      recordBtn?.classList.remove("is-recording");
      miniRecBtn?.classList.remove("is-recording");
      document.body.classList.remove("is-recording");
      if (recordBtn) {
        (recordBtn.querySelector(".mb-recordBtn__label") as HTMLElement).textContent = "Record";
      }
    } else {
      // Start recording: separate video and audio streams.
      // File / URL sources put their audio in `recordStream` (a dedicated
      // MediaStreamAudioDestinationNode); mic / tab sources put it in
      // `stream` directly. We prefer recordStream so file playback
      // captures sound; without this fallback, recorded MP4s of file
      // sources are silent.
      try {
        const audioInfo = engine.info();
        const armed = getSocialPreset(armedSocialPresetId);
        const outputSize = armed
          ? { width: armed.outputWidth, height: armed.outputHeight }
          : getSocialOutputSize(frame.dataset.aspect ?? "fit");
        compositor.setOutputSize(
          outputSize?.width ?? null,
          outputSize?.height ?? null,
        );
        await engine.resume();
        if (audioInfo.mediaElement?.paused) {
          await audioInfo.mediaElement.play();
        }
        recorder.start({
          videoStream: compositor.captureStream(60),
          audioStream: audioInfo.recordStream ?? audioInfo.stream ?? undefined,
        });
        compositor.start();
        recordingStartedAt = performance.now();
        recordBtn?.classList.add("is-recording");
        miniRecBtn?.classList.add("is-recording");
        // Auto-hide the HUD + chrome during recording for clean output.
        document.body.classList.add("is-recording");
        haptic("heavy");
        if (recordBtn) {
          (recordBtn.querySelector(".mb-recordBtn__label") as HTMLElement).textContent = "Stop";
        }
        timerHandle = setInterval(() => {
          if (!recorder.isRecording()) return;
          const elapsedMs = performance.now() - recordingStartedAt;
          const elapsedSec = elapsedMs / 1000;
          if (lengthCapMs > 0) {
            const remaining = Math.max(0, (lengthCapMs - elapsedMs) / 1000);
            setRecStatus(`● REC ${formatTime(elapsedSec)} / ${formatTime(lengthCapMs / 1000)} (−${formatTime(remaining)})`);
            if (elapsedMs >= lengthCapMs) void toggleRecord();
          } else {
            setRecStatus(`● REC ${formatTime(elapsedSec)}`);
          }
        }, 500);
      } catch (err) {
        compositor.stop();
        recordBtn?.classList.remove("is-recording");
        miniRecBtn?.classList.remove("is-recording");
        document.body.classList.remove("is-recording");
        setRecStatus(
          `Start failed: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    }
  };

  recordBtn?.addEventListener("click", () => void toggleRecord());

  // Mini-player record button mirrors the main one so users can record
  // without opening the drawer.
  miniRecBtn?.addEventListener("click", () => void toggleRecord());

  // Mini-player play/pause button. Finds the currently-bound audio
  // element from the engine and toggles its play state.
  miniPlayBtn?.addEventListener("click", async () => {
    haptic("light");
    const info = engine.info();
    const audio = info.mediaElement;
    if (!audio) {
      // No file/URL source loaded yet — open the drawer to the source tab.
      openDrawer();
      switchToTab("source");
      return;
    }
    try {
      await engine.resume();
      if (audio.paused) {
        await audio.play();
        if (miniPlayBtn) miniPlayBtn.textContent = "⏸";
      } else {
        audio.pause();
        if (miniPlayBtn) miniPlayBtn.textContent = "▶︎";
      }
    } catch {
      /* surface via source status if needed */
    }
  });

  // -------- Reel: snapshot --------
  $<HTMLButtonElement>("mb-snapshotBtn")?.addEventListener("click", () => {
    haptic("medium");
    const armed = getSocialPreset(armedSocialPresetId);
    const outputSize = armed
      ? { width: armed.outputWidth, height: armed.outputHeight }
      : getSocialOutputSize(frame.dataset.aspect ?? "fit");
    compositor.setOutputSize(
      outputSize?.width ?? null,
      outputSize?.height ?? null,
    );
    void compositor.snapshot().then((blob) => {
      if (blob) void offerShareOrDownload(blob, suggestFilename("png"));
    });
  });

  // -------- Now Playing overlay --------
  const overlayEnabled = $<HTMLInputElement>("mb-overlayEnabled");
  const overlayTitle = $<HTMLInputElement>("mb-overlayTitle");
  const overlayArtist = $<HTMLInputElement>("mb-overlayArtist");
  const overlayFont = $<HTMLSelectElement>("mb-overlayFont");
  const overlayFontScale = $<HTMLInputElement>("mb-overlayFontScale");
  const overlayFontScaleVal = $<HTMLElement>("mb-overlayFontScaleVal");

  if (overlayEnabled) overlayEnabled.checked = overlayCfg.enabled;
  if (overlayTitle) overlayTitle.value = overlayCfg.title;
  if (overlayArtist) overlayArtist.value = overlayCfg.artist;
  if (overlayFont) {
    populateOverlayFontSelect(overlayFont);
    overlayFont.value = overlayCfg.fontFamily;
  }
  if (overlayFontScale) overlayFontScale.value = String(overlayCfg.fontScale);
  if (overlayFontScaleVal) overlayFontScaleVal.textContent = `${Math.round(overlayCfg.fontScale * 100)}%`;

  const syncOverlay = (): void => {
    const hasText = Boolean(
      overlayTitle?.value.trim() || overlayArtist?.value.trim(),
    );
    if (hasText && overlayEnabled && !overlayEnabled.checked) {
      overlayEnabled.checked = true;
    }
    overlayCfg = {
      ...overlayCfg,
      enabled: overlayEnabled?.checked ?? false,
      title: overlayTitle?.value.trim() ?? "",
      artist: overlayArtist?.value.trim() ?? "",
      fontFamily: isFontFamily(overlayFont?.value)
        ? overlayFont.value as FontFamily
        : overlayCfg.fontFamily,
      fontScale: clampOverlayFontScale(overlayFontScale?.value),
    };
    if (overlayFontScaleVal) overlayFontScaleVal.textContent = `${Math.round(overlayCfg.fontScale * 100)}%`;
    overlay.setConfig(overlayCfg);
    void ensureOverlayFontLoaded(overlayCfg.fontFamily);
    try {
      localStorage.setItem(LS_OVERLAY, JSON.stringify(overlayCfg));
    } catch {
      /* ignore */
    }
  };

  overlayEnabled?.addEventListener("change", syncOverlay);
  overlayTitle?.addEventListener("input", syncOverlay);
  overlayArtist?.addEventListener("input", syncOverlay);
  overlayFont?.addEventListener("change", syncOverlay);
  overlayFontScale?.addEventListener("input", syncOverlay);
}

